import { Action } from 'util/domain';
import { 
    ShareBaseDocument,
    ObjectType,
    ProgressEventListener,
    UploadFile,
    UploadOptions,
    UploadState,
    uploadStates
} from 'phoenix/domain';
import { AuthService } from 'phoenix/services';
import { IUriPathService } from 'phoenix/services/uriPathService';
import { 
    UploadError
} from 'phoenix/domain';
import { UploadExceptionFactory } from 'phoenix/factories/upload-exception-factory';

enum ErrorCodes {
    ERR_EXTENSION_MISMATCH
}

export interface UploadItemCallback {
    (file?:UploadItem):void;
}

interface UploadErrorResponse {
    StatusCode: string;
    Message: string;
    Parameters: any
}

type UploadResponse = UploadErrorResponse | ShareBaseDocument;
function isUploadError(res: UploadResponse): res is UploadErrorResponse {
    return Object.prototype.hasOwnProperty.call(res, 'StatusCode');
}

/**
 * Abstracts the process of uploading a file to ShareBase and creating the ShareBase document
 * metadata around that file.
 */
export class UploadItem {
    private _status: UploadState = uploadStates.queued;
    /**
     * Consumer-provided function to be called when the upload for this item has completed.
     */
    private completionCallback:(response:any) => void = angular.noop;

    /**
     * Consumer-provided function to be called when the upload for this item has failed for any reason.
     */
    private errorCallback:(e:Error) => void = angular.noop;    
    /**
     * Consumed provided function to be called each time a multi-part upload completes a chunk.
     */
    private progressCallback:(percent:number) => void = angular.noop;
    /**
     * Current status of the upload item. Can be one of the
     * values described in @see uploadStates 
     */
    public get status(): UploadState { return this._status; }

    /**
     * Offset used for reading the file's data blob.
     */
    private offset:number = 0;
    
    /**
     * Number of times the the item tried to upload its file data.
     */
    private attemptNumber:number = 0;

    /**
     * Holds the last XHR response from the server
     */
    private response:UploadResponse;

    /**
     * The sharebase file ref for uploading a chunked document.
     */
    private fileRef:any;

    /**
     * Holds the current XHR request for uploading the file data.
     */
    private xhr:XMLHttpRequest;

    // The below properties are used to normalize file attributes.
    /**
     * Normalized name used for the new ShareBase document. Prefers a name
     * passed in by consumer, but will default to the file name.
     */
    public readonly name:string;

    /**
     * MIME type of the file being uploaded.
     */
    public readonly type:string;

    /**
     * Size in bytes of the file being uploaded.
     */
    public readonly size:number;

    /**
     * Number of bytes that have been upload to the server.
     */
    public loaded:number = 0;

    /**
     * Index of the item in the upload service file queue.
     */
    public queueSlot:number = -1;

    /**
     * Will be set to true if the file will need to be uploaded
     * in chunks based on the configured chunk threshold option.
     */
    public readonly isChunkMode: boolean;


    /**
     * Create a new @see UploadItem
     * 
     * @param fsEntry The file metadata
     * @param uploadCallback The upload item callback that provides operations that the object will call into
     * during the upload process
     * @param options File upload configuration
     */
    constructor(
        private pxAuthService:AuthService,
        private pxUriPathService:IUriPathService,
        private pxUploadExceptionFactory:UploadExceptionFactory,
        private readonly fsEntry:UploadFile, 
        private uploadCallback:UploadItemCallback, 
        private options:UploadOptions
    ) {
        this.isChunkMode = fsEntry.file.size > options.chunkThreshold;
        this.name = fsEntry.name;
        this.type = fsEntry.file.type;
        this.size = fsEntry.file.size;
    }

    /**
     * Initiate the upload process for an instance of @see UploadItem.
     */
    public send() {
        if (this.isChunkMode) {
            this.initiateChunkUpload();
        } else {
            this.sendMultiPart();
        }
    }

    /**
     * Cancel upload for the current object's file.
     */
    public cancel() {
        this.xhr.abort();
        this._status = uploadStates.cancelled;
    }

    /**
     * Register a handler to be called on error.
     */
    public onError(callback: (e:Error) => void){
        this.errorCallback = callback;
        return this;
    } 

    /**
     * Register a handler to be called when this item has finished uploading.
     */
    public onComplete(callback:Action) {
        this.completionCallback = callback;
        return this;
    }

    public onProgress(callback: (percent:number) => void){
        this.progressCallback = callback;
        return this;
    }
    /**
     * Changes the objects @see status when it begins uploading.
     */
    private onLoadstart(): void {
        this._status = uploadStates.uploading;
    }

    /**
     * Gets called on XHR progress events to upload the instances @see loaded state.
     * 
     * @param e The ProgressEvent provided by the XHR request.
     */
    private onXhrProgress(e: ProgressEvent): void {
        if (e.lengthComputable) {
            if (this.isChunkMode) {
                let lastChunk = (this.offset + this.options.chunkSize) > this.size;
                if (lastChunk && e.loaded === e.total) {
                    this.loaded += (this.size - this.loaded) / 2;
                } else {
                    this.loaded = this.offset + e.loaded;
                }
            } else {
                if (e.loaded === e.total) {
                    this.loaded += (e.total - this.loaded) / 2;
                } else {
                    this.loaded = e.loaded;
                }
            }

            this.progressCallback(this.loaded);
        }
    }

    /**
     * XHR callback for when the request completes. This method is used only
     * when the file is being uploaded using the multi-part method.
     * 
     * @param e The event object provided by the XHR request.
     */
    private onXhrComplete(e: Event): void {
        if (this._status === uploadStates.uploading) {
            this._status = uploadStates.done;
        }
        if (this.xhr.status !== 200) {
            this.onXhrError(e);
        } else {
            this.response = JSON.parse(this.xhr.response);
            this.uploadCallback(this);
            this.completionCallback(this.response);
        }
    }

    /**
     * XHR callback for when a XHR request for a single chunk gets completed. This
     * method will only execute if the file is being uploaded using the chunking method.
     * 
     * @param e The event object provided by the XHR request.
     */
    private onChunkComplete(e): void {
        if (this.xhr.status !== 200) {
            this.onXhrError(e);
        } else if (this._status === uploadStates.cancelled) {
            this.uploadCallback(this);
        } else {
            // check to see if the server and client agree on how much data has
            // transferred as a sanity check
            let rsp = JSON.parse(this.xhr.response);
            this.offset += this.options.chunkSize;
            let mySize = this.offset >= this.size ? this.size : this.offset;

            if (rsp.CurrentSize !== mySize) {
                if (this.attemptNumber < this.options.maxRetries) {
                    this.attemptNumber++;
                    this.offset = 0;
                    this.loaded = 0;
                    this.send();
                } else {
                    this._status = uploadStates.cancelled;
                    this.uploadCallback(this);
                    this.errorCallback(new Error(`File ${this.fsEntry.name} encountered a sync error during upload.`));
                }
            } else if (this.offset >= this.size) {
                // upload of data is complete.  The file ref now needs to be posted
                // to the desired uri
                this.completeChunkUpload();
            } else {
                this.sendFileChunk();
            }
        }
    }

    /**
     * An XHR success callback for when the item has successfully created the ShareBase fileref object
     * for uploading in chunk mode
     * 
     * @param e An event that is provided by the XHR request.
     */
    private onFileRef(e: Event): void {
        this.fileRef = JSON.parse(this.xhr.response);
        this.sendFileChunk();
    }

    /**
     * Error callback used by all XHR requests.
     * 
     * @param e Event provided by the XHR request.
     */
    private onXhrError(e:Event): void {
        let err:UploadErrorResponse = this.response as UploadErrorResponse;
        if (!angular.isDefined(err)){
            this._status = 'error';
            this.uploadCallback(this);
            if (this.xhr.status === 404){
                let uploadError = this.pxUploadExceptionFactory.create(UploadError.FILE_NOT_FOUND, this.fsEntry.name);
                this.errorCallback(uploadError);
            } else {
                let uploadError = this.pxUploadExceptionFactory.create(UploadError.DEFAULT, this.fsEntry.name);
                this.errorCallback(uploadError);
            }

        } else {
            // No sense in trying to reupload document on extension mismatch, we already know it will fail
            if (this.attemptNumber < this.options.maxRetries && err.StatusCode !== ErrorCodes[ErrorCodes.ERR_EXTENSION_MISMATCH]) {
                this.attemptNumber++;
                this.offset = 0;
                this.loaded = 0;
                this.send();
            } else {
                this._status = 'error';
                this.uploadCallback(this);
            }


            let uploadError;
            if (err.StatusCode === ErrorCodes[ErrorCodes.ERR_EXTENSION_MISMATCH]) {
                uploadError = this.pxUploadExceptionFactory.create(UploadError.FILE_TYPE_MISMATCH, this.fsEntry.name);
            } 
            else {
                uploadError = this.pxUploadExceptionFactory.create(UploadError.DEFAULT, this.fsEntry.name);
            }

            this.errorCallback(uploadError);
        }

    }

    /**
     * Prepares an XHR request for upload based on whether the item is in chunk mode or not.
     * 
     * @param method The HTTP request method to use
     * @param url The target URL of the request
     * @param onComplFn A callback for when the request completes.
     * @param onProgFn An optional callback for the requests progress event. Only useful if the item is in chunk mode.
     */
    private prepareRequest(method: string, url: string, onComplFn: EventListenerOrEventListenerObject, onProgFn?: ProgressEventListener): void {
        let xhr = new XMLHttpRequest();
        let tokenHeader = `${this.pxAuthService.token.type} ${this.pxAuthService.token.value}`;

        xhr.addEventListener('loadstart', () => this.onLoadstart(), false);
        xhr.addEventListener('load', onComplFn, false);
        xhr.addEventListener('error', (e) => this.onXhrError(e), false);

        if (typeof onProgFn === 'function') {
            xhr.upload.addEventListener('progress', onProgFn, false);
        }

        xhr.open(method, url, true);
        xhr.setRequestHeader('Authorization', tokenHeader);
        xhr.setRequestHeader('Accept', '*/*');
        xhr.setRequestHeader('x-sharebase-app', 'WEB');

        this.xhr = xhr;
    }

    /**
     * Uploads the file using multi-part.
     */
    private sendMultiPart(): void {
        let document = { DocumentName: this.name };

        let formData = new FormData();
        formData.append('file', this.fsEntry.file, this.name);
        formData.append('metadata', JSON.stringify(document));

        this.prepareRequest('POST', this.fsEntry.uploadUri, (e) => this.onXhrComplete(e), (e) => this.onXhrProgress(e as ProgressEvent));
        this.xhr.send(formData);
    }

    /**
     * Create a new upload XHR request for the next file chunk.
     */
    private initiateChunkUpload(): void {
        let uri = this.fsEntry.objectType === ObjectType.FOLDER ? '/f/{id}/temp' : '/documents/{id}/revisions/temp';
        uri = this.pxUriPathService.resolvePath(uri, { id: this.fsEntry.objectId });

        this.prepareRequest('POST', uri, (e) => this.onFileRef(e));
        this.xhr.setRequestHeader('Content-Type', 'application/json');
        this.xhr.send(JSON.stringify({ FileName: this.name }));
    }

    /**
     * Upload the next file chunk.
     */
    private sendFileChunk() {
        let endOffset = this.offset + this.options.chunkSize;
        if (endOffset > this.size) {
            endOffset = this.size;
        }

        let data = this.readSlice(this.offset, endOffset);
        this.prepareRequest('PATCH', this.fileRef.Links.Location, (e) => this.onChunkComplete(e), (e) => this.onXhrProgress(e));
        this.xhr.setRequestHeader("Content-Type", "application/octet-stream");
        this.xhr.send(data);
    }

    /**
     * Makes a request to the server to commit the new file if it was uploaded using chunk mode.
     * This request will create the actual ShareBase document object.
     */
    private completeChunkUpload() {
        let document = { DocumentName: this.name };

        let formData = new FormData();
        formData.append('metadata', JSON.stringify(document));

        this.prepareRequest('POST', this.fsEntry.uploadUri, (e) => this.onXhrComplete(e));
        this.xhr.setRequestHeader('x-sharebase-fileref', JSON.stringify(this.fileRef));
        this.xhr.send();
    }

    /**
     * Get a segment of the file data based on the provided start and end bytes
     * 
     * @param startByte Indicates where to start reading the file data.
     * @param endByte Indicates where the stop reading the file data.
     */
    private readSlice(startByte: number, endByte: number): Blob {
        let fnName = 'slice';
        let f: any = this.fsEntry.file;
        
        if (f.slice) {
            fnName = 'slice';
        } else if (f.mozSlice) {
            fnName = 'mozSlice';
        } else if (f.webkitSlice) {
            fnName = 'webkitSlice';
        }

        return f[fnName](startByte, endByte);
    }
}


// WEBPACK FOOTER //
// ./src/phoenix/domain/upload-item.ts